/**
 * Profile Type Helpers
 *
 * Utilities for normalizing and styling profile types across the application.
 * Profile types in the database are stored as "type_media", "type_promoter_pr", etc.
 * These helpers convert them to the normalized form used by UI components.
 */

/**
 * Normalize a profile type from database format to UI format.
 * Simply strips the "type_" prefix to match CSS class naming.
 *
 * @param {string} typeOfProfile - Database profile type (e.g., "type_media", "type_promoter_expert")
 * @returns {string|null} Normalized profile type (e.g., "media", "promoter_expert") or null if unknown
 *
 * @example
 * normalizeProfileType("type_media") // => "media"
 * normalizeProfileType("type_promoter_pr") // => "promoter_pr"
 * normalizeProfileType("type_promoter_expert") // => "promoter_expert"
 * normalizeProfileType("type_promoter_hybrid") // => "promoter_hybrid"
 */
export function normalizeProfileType(typeOfProfile) {
  if (!typeOfProfile) return null;

  // Handle database format with "type_" prefix
  if (typeOfProfile.startsWith("type_")) {
    return typeOfProfile.replace("type_", "");
  }

  // Handle ID class format (e.g., "Id::Profile::Media")
  if (typeOfProfile.includes("::")) {
    const parts = typeOfProfile.split("::");
    const lastPart = parts[parts.length - 1].toLowerCase();

    switch (lastPart) {
      case "media":
        return "media";
      case "pr":
        return "promoter_pr";
      case "expert":
        return "promoter_expert";
      case "hybrid":
        return "promoter_hybrid";
      default:
        return null;
    }
  }

  // Already in normalized form
  const validTypes = ["media", "promoter_pr", "promoter_expert", "promoter_hybrid"];
  if (validTypes.includes(typeOfProfile)) {
    return typeOfProfile;
  }

  return null;
}

/**
 * Get badge styling for a profile type.
 * Uses colors from globalThis.Qwoted for consistency with the design system.
 *
 * @param {string} normalizedProfileType - Normalized profile type from normalizeProfileType()
 * @returns {object} Badge styling with { backgroundColor, textColor, label }
 *
 * @example
 * const badge = getProfileTypeBadge("media");
 * // => { backgroundColor: "#4d7abc", textColor: "white", label: "Media" }
 */
export function getProfileTypeBadge(normalizedProfileType) {
  switch (normalizedProfileType) {
    case "media":
      return {
        label: "Media",
        backgroundColor: globalThis.Qwoted?.COLOR_MEDIA || "#4d7abc",
        textColor: "white",
      };
    case "promoter_pr":
      return {
        label: "PR",
        backgroundColor: globalThis.Qwoted?.COLOR_PR || "#a0b23e",
        textColor: "white",
      };
    case "promoter_expert":
      return {
        label: "Expert",
        backgroundColor: globalThis.Qwoted?.COLOR_EXPERT || "#f7553f",
        textColor: "white",
      };
    case "promoter_hybrid":
      return {
        label: "Hybrid",
        backgroundColor: globalThis.Qwoted?.COLOR_EXPERT_PR || "#dbb058",
        textColor: "white",
      };
    default:
      return {
        label: "Unknown",
        backgroundColor: "#6c757d",
        textColor: "white",
      };
  }
}

/**
 * Check if a profile type is a promoter type (non-media).
 *
 * @param {string} normalizedProfileType - Normalized profile type
 * @returns {boolean} True if the profile type is a promoter variant (PR, Expert, or Hybrid)
 */
export function isPromoterType(normalizedProfileType) {
  return normalizedProfileType?.startsWith("promoter_") || false;
}

/**
 * Check if a profile type is a media type.
 *
 * @param {string} normalizedProfileType - Normalized profile type
 * @returns {boolean} True if the profile type is media
 */
export function isMediaType(normalizedProfileType) {
  return normalizedProfileType === "media";
}
